import { DT, PF_HW } from '../constants';
import { enemyFireRateMul } from '../constraints';
import { Pattern, type EmitterDef, type EnemyDef } from '../defs';
import { Ev } from '../events';
import {
  BULLET_SPEED_MUL,
  clampDifficulty,
  FAN_ROTATES,
  FIRE_RATE_MUL,
  SEAL_RADIUS,
  VETERAN_FAN_STEP_DEG,
} from '../difficulty';
import { FEEL } from '../FeelConstants';
import { atan2, cos, DEG, PI, sin } from '../math';
import type { World } from '../World';
import { CURVE_LIFE, BEAM_TELEGRAPH_Y, EbMode } from './enemyBulletSystem';

/* ═══════════════════════════════════════════════════════════════════════════
   L'émission de projectiles, désormais pilotée par les DONNÉES.

   Le moteur connaît cinq formes de tir — aucune, visé, éventail statique,
   anneau, mur percé — et le contenu choisit laquelle, avec quels paramètres.
   Ajouter un ennemi qui tire différemment ne demande pas d'y toucher ; ajouter
   une nouvelle FORME de tir, si, et c'est exactement la frontière qu'on veut :
   entre « nouveau contenu » et « nouvelle capacité du moteur ».
   ═══════════════════════════════════════════════════════════════════════════ */

/** Émet un projectile et rend son slot, ou -1 si le pool est saturé.
 *
 *  Le slot est rendu pour que les patterns à CONDUITE puissent équiper le
 *  projectile juste après sa naissance (`w.eb.behave(...)`). Les patterns
 *  balistiques l'ignorent — ils n'ont rien à dire de plus que « il part de là,
 *  vers là ». Le refus reste silencieux côté pool : c'est `emit` qui
 *  comptabilise l'overflow, jamais l'appelant. */
function emit(
  w: World,
  ex: number,
  ey: number,
  angle: number,
  speed: number,
  family: number,
): number {
  const slot = w.eb.spawn(
    ex,
    ey,
    cos(angle) * speed,
    sin(angle) * speed,
    FEEL.enemyBulletRadius,
    1,
    family,
  );
  if (slot < 0) w.overflow.enemyBullets++;
  return slot;
}

/** Émetteur actif d'un ennemi selon sa PHASE de combat. La phase 0 (ou tout
 *  ennemi sans `phases`) tire l'émetteur de base ; chaque phase suivante impose
 *  le sien. Borné par prudence : une phase hors plage retombe sur la base. */
export function activePhaseEmitter(def: EnemyDef, phase: number): EmitterDef {
  if (phase > 0 && def.phases && phase <= def.phases.length) {
    return def.phases[phase - 1]!.emitter;
  }
  return def.emitter;
}

/** Multiplicateur de CADENCE de la phase courante (enrage). 1 hors phase. */
function phaseSpeedMul(def: EnemyDef, phase: number): number {
  if (phase > 0 && def.phases && phase <= def.phases.length) {
    return def.phases[phase - 1]!.speedMul ?? 1;
  }
  return 1;
}

export function emitterSystem(w: World): void {
  const en = w.enemies;
  const p = w.player;
  const diff = clampDifficulty(w.run.difficulty);

  /* ⚠️ Le rayon de scellement vient de la DIFFICULTÉ, pas de `FEEL`.
     C'est le levier de difficulté le plus important du jeu et le moins visible :
     il ne change aucun chiffre affiché, il change la taille de la porte de
     sortie d'un joueur acculé (140 / 100 / 70 u selon le palier). */
  const sealRadius = SEAL_RADIUS[diff]!;
  const sealR2 = sealRadius * sealRadius;
  // Rotation des éventails statiques : nulle sauf en Vétéran. Voir
  // `src/sim/difficulty.ts` pour la raison — c'est le changement de NATURE
  // qu'exige le plan, qui accompagne les curseurs ci-dessous sans s'y réduire.
  const fanStep = FAN_ROTATES[diff]! ? VETERAN_FAN_STEP_DEG * DEG : 0;

  /* Cadence et vitesse combinent le réglage GLOBAL (`balance`, rechargeable à
     chaud) et le PALIER choisi par le joueur. Les deux se multiplient : le
     premier sert à équilibrer le jeu, le second à choisir sa dureté, et
     confondre les deux rendrait tout réglage global dépendant du palier de
     celui qui l'a mesuré. */
  /* ⚠️ La contrainte « Marée noire » vient EN TROISIÈME FACTEUR, jamais en
     remplacement du deuxième. Les deux touchent la même grandeur pour des
     raisons différentes : le palier dit à quel point le joueur veut que le jeu
     soit dur, la contrainte dit qu'il en veut +35 % de plus contre des crédits.

     Une contrainte qui POSERAIT la cadence à 1,35 ferait passer la Recrue de
     0,70 à 1,35 et le Vétéran de 1,20 à 1,35 : elle adoucirait relativement le
     palier le plus dur tout en le payant au même tarif majoré, et le chemin le
     plus rentable du jeu deviendrait le plus facile. En multiplicatif, elle vaut
     exactement +35 % à chaque palier et l'ordre des trois reste intact. */
  /* Le RANG CACHÉ (§6.10) se greffe ICI, sur la PRESSION — la cadence — et
     jamais sur les PV ni les récompenses : c'est la frontière que le palier de
     difficulté, lui, a le droit de franchir, pas le curseur invisible.

     `rankMul` (0,85 → 1,6) est amorti de moitié : au pic, +30 % de cadence, du
     même ordre que l'écart Opérateur → Vétéran, jamais le +60 % brut qui noierait
     l'écran et ferait sauter le plafond de densité. Côté clémence, −7,5 % au
     plancher, une vraie main tendue à qui s'enfonce.

     Le rang ne touche délibérément PAS `speedMul` : au-delà d'un point une balle
     plus rapide passe sous le temps de réaction (~250 ms, voir BULLET_SPEED_MUL)
     et la mort cesse d'être attribuable — exactement ce qu'un curseur invisible
     doit s'interdire de produire. */
  const rankCadence = 1 + (w.run.rankMul - 1) * 0.5;
  const rate =
    w.defs.balance.enemyFireRate *
    FIRE_RATE_MUL[diff]! *
    enemyFireRateMul(w.run.constraints) *
    rankCadence;
  const speedMul = w.defs.balance.bulletSpeed * BULLET_SPEED_MUL[diff]!;

  for (let i = 0; i < en.count; i++) {
    const s = en.at(i);
    const def = w.defs.enemies[en.kind[s]!];
    if (!def) continue;
    // Émetteur de la PHASE de combat courante (boss) : la phase 0 est le
    // comportement de base, chaque phase suivante prend le relais avec sa propre
    // attaque et son éventuel enrage de cadence. Coût nul sans `phases`.
    const em = activePhaseEmitter(def, en.phase[s]!);
    if (em.pattern === Pattern.None) continue;
    const phaseCadence = phaseSpeedMul(def, en.phase[s]!);

    const cd = en.fireCd[s]! - DT;
    en.fireCd[s] = cd;
    if (cd > 0) continue;

    // ⚠️ Le cooldown est réarmé QUOI QU'IL ARRIVE, même si le tir est annulé.
    // C'est le point subtil du bullet sealing : sans réarmement, un ennemi
    // bloqué au contact accumulerait ses volées et lâcherait tout d'un coup à
    // l'instant où le joueur s'écarte — soit exactement la mort injuste que le
    // mécanisme existe pour supprimer.
    en.fireCd[s] = cd + em.every / ((rate > 0 ? rate : 1) * phaseCadence);

    const ex = en.x[s]!;
    const ey = en.y[s]!;

    if (p.dead) continue;
    if (en.telegraph[s]! > 0) continue;
    if (en.age[s]! < 0.6) continue;

    const dx = p.x - ex;
    const dy = p.y - ey;
    if (dx * dx + dy * dy < sealR2) continue;

    const speed = em.speed * speedMul;
    const spread = em.spreadDeg * DEG;

    // Un vrai tir part : on le signale à la présentation (audio). `a` = pattern.
    w.events.push(Ev.EnemyFired, em.pattern, ex, ey, 0);

    switch (em.pattern) {
      case Pattern.Aimed: {
        let ang = atan2(dy, dx);
        // Cessez-le-feu des bords : on dévie la balle DU CÔTÉ DU MUR, ce qui
        // pousse le joueur vers le centre où il a de la place. L'inverse le
        // clouerait contre la paroi (voir tests/sim/lifecycle.test.ts).
        if (p.x > FEEL.edgeCeasefireX) ang += FEEL.edgeCeasefireBiasDeg * DEG;
        else if (p.x < -FEEL.edgeCeasefireX) ang -= FEEL.edgeCeasefireBiasDeg * DEG;

        for (let k = 0; k < em.count; k++) {
          const t = em.count === 1 ? 0 : k / (em.count - 1) - 0.5;
          emit(w, ex, ey, ang + t * spread, speed, em.family);
        }
        break;
      }

      case Pattern.Fan: {
        // Éventail STATIQUE, vers le bas, indépendant du joueur. Il interdit
        // une zone au lieu de poursuivre — c'est ce qui donne au terrain une
        // géographie, et donc un sens au déplacement.
        //
        // ⚠️ En Vétéran, il TOURNE d'une volée à l'autre. La figure reste la
        // même, mais ses couloirs se déplacent : on ne peut plus mémoriser une
        // place sûre et s'y installer, il faut relire à chaque volée. C'est
        // exactement ce que le plan entend par « changer la nature du pattern »
        // — le nombre de projectiles, lui, ne bouge pas, donc la densité reste
        // dans l'enveloppe mesurée et le budget de frame tient.
        let base = -PI / 2;
        if (fanStep !== 0) {
          const phase = en.emitPhase[s]! + fanStep;
          en.emitPhase[s] = phase;
          base += phase;
        }
        for (let k = 0; k < em.count; k++) {
          const t = em.count === 1 ? 0 : k / (em.count - 1) - 0.5;
          emit(w, ex, ey, base + t * spread, speed, em.family);
        }
        break;
      }

      case Pattern.Ring: {
        // La rotation par volée fait que deux anneaux successifs ne se
        // superposent jamais : la figure reste lisible comme une fleur au lieu
        // de devenir un mur.
        const phase = en.emitPhase[s]! + em.stepDeg * DEG;
        en.emitPhase[s] = phase;
        const step = (PI * 2) / em.count;
        for (let k = 0; k < em.count; k++) {
          emit(w, ex, ey, phase + k * step, speed, em.family);
        }
        break;
      }

      case Pattern.Wall: {
        /* ═══ Le mur percé ═══════════════════════════════════════════════
           Une rangée horizontale d'emplacements régulièrement espacés, dont
           UN reste vide. Le vide glisse d'une volée à l'autre.

           ── Le compteur de volée ──
           Réutilise `emitPhase`, le champ que Ring emploie déjà pour sa
           rotation. Même besoin — « où en est cette entité dans sa séquence
           d'émission ? » — donc même case mémoire. Seule l'unité diffère :
           radians pour Ring, indice de volée ici. Ajouter un second champ
           aurait dupliqué la remise à zéro au recyclage de slot, c'est-à-dire
           créé un deuxième endroit où oublier de la faire.

           ── Le va-et-vient ──
           `tri` parcourt 0,1,2,3,2,1 puis recommence, ce qui donne trois pas
           dans un sens suivis de trois dans l'autre — jamais quatre, jamais
           deux. C'est la propriété qui compte : le joueur qui a compris le sens
           du glissement peut se déplacer AVEC lui pendant trois murs, et c'est
           ce qui transforme le Tisseur en danse plutôt qu'en série d'arrêts.

           ── ⚠️ Interdiction du couloir unique (PLAN.md §6.5) ──
           Un mur pleine largeur percé d'un seul trou viole la règle des deux
           couloirs viables de 110 u : il n'en laisse qu'un.

           Trois issues étaient possibles — deux trous, un trou plus large, ou
           un mur qui ne couvre pas toute la largeur. C'est la TROISIÈME qui est
           retenue, et pour une raison de lisibilité plutôt que d'arithmétique :
           les deux premières demandent au joueur de compter des trous au milieu
           d'une ligne de projectiles, sous pression, en 0,3 s. Les extrémités
           d'un mur, elles, se voient d'un seul coup d'œil — on lit « ça finit
           là » sans rien dénombrer.

           Le contenu doit donc respecter :  width ≤ PF_W − 2 × 110
           soit 680 u pour un terrain de 900. Le Tisseur est réglé à 650, ce qui
           laisse 125 u de flanc de chaque côté quand il est centré, plus le
           trou : trois couloirs, pas deux. La marge est délibérée — deux
           couloirs pile est un minimum réglementaire, pas une cible.

           ⚠️ PIÈGE POUR L'AUTEUR DE CONTENU : la règle porte sur UN mur. Deux
           Tisseurs côte à côte peuvent parfaitement sceller le terrain. Ils se
           placent en quinconce, jamais en ligne.

           ── La largeur du trou est DÉRIVÉE ──
           On retire l'emplacement le plus proche du centre du trou : le vide
           mesure donc exactement deux pas de mur, toujours, quelle que soit sa
           position. Une largeur donnée en unités et découpée dans la grille
           aurait produit un trou variant de 130 à 195 u selon l'alignement —
           une garantie de plancher plutôt qu'une promesse tenue, et le joueur
           aurait senti l'irrégularité sans pouvoir la nommer. */
        const n = em.count;
        const width = em.width ?? 0;
        if (n < 2 || width <= 0) break;

        const k = en.emitPhase[s]!;
        en.emitPhase[s] = k + 1;

        const spacing = width / (n - 1);
        const left = ex - width * 0.5;

        // 0,1,2,3,2,1 — trois pas montants, trois descendants.
        const m6 = k - Math.floor(k / 6) * 6;
        const tri = m6 <= 3 ? m6 : 6 - m6;
        // Centré sur l'émetteur : le va-et-vient balaie ±1,5 pas de glissement
        // autour de lui, au lieu de dériver toujours du même côté.
        const holeX = ex + (tri - 1.5) * (em.slide ?? 0);

        // Quantification sur la grille : c'est l'emplacement le plus proche qui
        // reste vide. Écrêté aux bornes pour qu'un glissement démesuré ne
        // produise pas un mur SANS trou — la panne serait silencieuse et
        // parfaitement injouable.
        let hj = Math.round((holeX - left) / spacing);
        if (hj < 0) hj = 0;
        else if (hj > n - 1) hj = n - 1;

        for (let j = 0; j < n; j++) {
          if (j === hj) continue;
          const bx = left + j * spacing;
          // Hygiène de pool : un mur émis près d'un bord déborde du terrain, et
          // ces projectiles-là ne seront jamais vus. Les émettre puis les faire
          // éliminer par `motionSystem` consommerait des slots au moment précis
          // où la densité est maximale.
          if (bx < -PF_HW || bx > PF_HW) continue;
          emit(w, bx, ey, -PI / 2, speed, em.family);
        }
        break;
      }

      case Pattern.Homing: {
        /* ═══ Le missile guidé ════════════════════════════════════════════
           Lancé DROIT vers le bas — jamais visé. C'est délibéré et c'est ce que
           mesure le test de l'arme : un tir droit manque un joueur décalé, et
           c'est le guidage — lui seul — qui rattrape l'écart. Un lancer visé
           toucherait déjà, et le guidage ne prouverait rien.

           L'éventail OUVRE les missiles (comme le Traqueur du joueur) pour que
           la courbe se VOIE : alignés, ils convergeraient invisiblement et le
           joueur ne verrait qu'un tir groupé un peu lent. Une mécanique qu'on ne
           peut pas percevoir n'existe pas.

           La borne du virage vit dans les DONNÉES (`turnDeg`) mais la portée
           d'acquisition et le « n'engage que par au-dessus » vivent dans le
           moteur (enemyBulletSystem) : ce sont eux qui gardent l'esquive vivante,
           et les exposer au contenu reviendrait à permettre un missile
           inévitable sans qu'une revue de données le laisse deviner. */
        const turn = (em.turnDeg ?? 0) * DEG;
        for (let k = 0; k < em.count; k++) {
          const t = em.count === 1 ? 0 : k / (em.count - 1) - 0.5;
          const slot = emit(w, ex, ey, -PI / 2 + t * spread, speed, em.family);
          if (slot >= 0) w.eb.behave(slot, EbMode.Homing, turn, 0, 0);
        }
        break;
      }

      case Pattern.Spiral: {
        /* ═══ La spirale ══════════════════════════════════════════════════
           Un anneau, mais dont chaque projectile COURBE : la figure radiale se
           déroule en spirale. Réutilise `emitPhase` exactement comme Ring — même
           besoin (« où en est cette entité dans sa séquence ? »), même case
           mémoire — pour que deux pulsations ne se superposent pas.

           La courbe est une géométrie prévisible, pas une poursuite : le joueur
           la lit et anticipe le point d'arrivée. « Esquiver là où la balle est »
           l'y jette ; « esquiver là où elle SERA » l'en sort — c'est la leçon. */
        const curve = (em.turnDeg ?? 0) * DEG;
        const phase = en.emitPhase[s]! + em.stepDeg * DEG;
        en.emitPhase[s] = phase;
        const stepA = (PI * 2) / em.count;
        for (let k = 0; k < em.count; k++) {
          const slot = emit(w, ex, ey, phase + k * stepA, speed, em.family);
          if (slot >= 0) w.eb.behave(slot, EbMode.Curve, curve, CURVE_LIFE, 0);
        }
        break;
      }

      case Pattern.Beam: {
        /* ═══ Le faisceau persistant ══════════════════════════════════════
           Une COLONNE de segments ancrés SOUS l'émetteur, tous nés au même
           instant, tous repositionnés chaque tick par `enemyBulletSystem`. Leur
           vitesse est nulle : le faisceau n'a pas de temps de vol, c'est sa
           raison d'être. Sa persistance vient de `beamLife`, son ancrage de
           l'uid de l'émetteur, porté dans `lastUid`.

           ⚠️ Le pas de colonne doit rester sous le diamètre d'un segment, sinon
           le faisceau devient un pointillé — un trait discontinu laisse passer un
           joueur entre deux points, ce qui se lit comme un rayon « qui ne touche
           pas ». Le diamètre vaut 2 × `enemyBulletRadius` (22 u) ; le contenu
           dimensionne `beamLen`/`count` en conséquence. */
        const n = em.count;
        const len = em.beamLen ?? 0;
        const life = em.beamLife ?? 0;
        if (n < 1 || len <= 0 || life <= 0) break;
        /* Avertissement pour la portion qui plonge SOUS −180 (faisceau pleine
           hauteur). `warnDur = 0` → faisceau classique du Sabreur, inchangé.
           Le segment vit `life + warnDur` : l'avertissement s'ajoute à la
           persistance, il ne la rogne pas. */
        const warnDur = em.beamWarn ?? 0;
        const totalLife = life + warnDur;
        const stepY = n > 1 ? len / (n - 1) : 0;
        const uid = en.uid[s]!;
        for (let k = 0; k < n; k++) {
          const off = k * stepY;
          const targetY = ey - off;
          /* Un segment dont la position PLEINE atteint sous −180 doit être
             télégraphié : il naît PARQUÉ à l'ancre (donc à `ey`, en haut) et n'y
             descendra qu'après `warnDur`. Les segments hauts, eux, sont létaux
             tout de suite — c'est licite au-dessus de −180, et c'est ce haut qui
             sert de télégraphe visible à la colonne. */
          const w0 = warnDur > 0 && targetY < BEAM_TELEGRAPH_Y ? warnDur : 0;
          const spawnY = w0 > 0 ? ey : targetY;
          const slot = emit(w, ex, spawnY, 0, 0, em.family);
          // Pool saturé : inutile de poser le reste du rayon, il serait troué.
          if (slot < 0) break;
          w.eb.behave(slot, EbMode.Beam, off, totalLife, 0);
          // `lastUid` porte ici l'uid de l'ÉMETTEUR — l'ancre du faisceau, pas un
          // ennemi frappé. `behave` ne le touche pas ; on l'écrit à la main.
          w.eb.lastUid[slot] = uid;
          w.eb.warn[slot] = w0;
        }
        break;
      }

      case Pattern.Burst: {
        /* ═══ La rafale ═══════════════════════════════════════════════════
           Une salve de `count` balles VISÉES, tirées une par une à `burstGap`
           d'intervalle, suivie de la longue pause d'`every`. `emitPhase` sert
           de compteur de tir DANS la salve — même case mémoire que Ring, Wall
           et Spiral, même besoin (« où en est cette entité dans sa
           séquence ? »), donc pas un champ de plus à remettre à zéro au
           recyclage.

           Le réarmement générique en tête de boucle a déjà posé
           `fireCd ≈ every` (la longue pause). On ne l'écrase QUE tant que la
           salve n'est pas terminée, en le ramenant à `burstGap`. Le cycle vaut
           donc `every + (count−1)·gap`, dominé par la pause : c'est ce qui
           borne la densité — une rafale ne sature pas l'écran à elle seule. */
        const k = en.emitPhase[s]!;
        let ang = atan2(dy, dx);
        // Cessez-le-feu des bords, comme Aimed : on pousse le joueur vers le
        // centre plutôt que de le clouer au mur.
        if (p.x > FEEL.edgeCeasefireX) ang += FEEL.edgeCeasefireBiasDeg * DEG;
        else if (p.x < -FEEL.edgeCeasefireX) ang -= FEEL.edgeCeasefireBiasDeg * DEG;
        // Léger balayage réparti sur la salve : la rafale devient un éventail
        // DANS LE TEMPS, que le joueur suit en tenant un déplacement au lieu
        // d'un pas de côté unique.
        const t = em.count <= 1 ? 0 : k / (em.count - 1) - 0.5;
        emit(w, ex, ey, ang + t * spread, speed, em.family);

        const nk = k + 1;
        if (nk >= em.count) {
          // Salve terminée : compteur à zéro, et on LAISSE la longue pause déjà
          // posée par le réarmement générique.
          en.emitPhase[s] = 0;
        } else {
          en.emitPhase[s] = nk;
          // Tir suivant : on écrase la longue pause par le petit intervalle. La
          // cadence de difficulté s'y applique comme partout ailleurs.
          const gap = em.burstGap ?? 0.06;
          en.fireCd[s] = gap / (rate > 0 ? rate : 1);
        }
        break;
      }

      default:
        break;
    }
  }
}
